import {Avatar, Button, Card, openDialog} from 'platform/components';
import {Box, Grid, HStack, Show, Text, ValueByDevice, VStack} from 'platform/foundation';

import {isEmpty, isNil, isNotNil} from 'ramda';

import {
  TelephonyCallDetail,
  TelephonyCallDetailRecord,
  useUnassignTelephonyCallRecordMutation,
} from '@dms/api/settings';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {suffixTestId, TestIdProps} from 'shared/web';

import {CallLinkedRecordTile} from '../../../components/CallLinkedRecordTile/CallLinkedRecordTile';
import {LinkCallRecordDialog} from '../../../components/LinkCallRecordDialog/LinkCallRecordDialog';

interface CallLinkedRecordsCardProps extends TestIdProps {
  call: TelephonyCallDetail;
  canLink: boolean;
  canUnlink: boolean;
}

const LINK_DIALOG_SIZE = 'xLarge' as const;
const GRID_COLUMNS: ValueByDevice<number> = [1, 2, 3];
/** Same height as a filled tile (avatar + flag + title), so the empty tile lines up with them. */
const PLACEHOLDER_CONTENT_HEIGHT = 14;

interface LinkRecordPlaceholderProps extends TestIdProps {
  onClick: () => void;
}

/** The blue dashed tile that opens the picker, as the record-audit team slots have it. */
function LinkRecordPlaceholder(props: LinkRecordPlaceholderProps) {
  return (
    <Box
      padding={3}
      height="100%"
      borderRadius="small"
      border="1px dashed"
      borderColor="palettes.blue.60.100"
      backgroundColor="palettes.blue.10.100"
      data-testid={suffixTestId('placeholder', props)}
    >
      <HStack spacing={3} align="center" height="100%" minHeight={PLACEHOLDER_CONTENT_HEIGHT}>
        <Avatar icon="content/link" size="default" />
        <Button
          variant="link"
          title={i18n.t('page.telephony.callDetail.linkRecord')}
          onClick={props.onClick}
          data-testid={suffixTestId('placeholder-button', props)}
        />
      </HStack>
    </Box>
  );
}

/**
 * The records the call is linked to, laid out like the record-audit team: a tile per record and,
 * for a viewer who may link, a dashed tile that opens the picker. A call with no customer has
 * nothing to pick from, so it shows the empty text instead.
 */
export function CallLinkedRecordsCard(props: CallLinkedRecordsCardProps) {
  const [unassignRecord, {isLoading: isUnlinking, originalArgs: unlinkArgs}] =
    useUnassignTelephonyCallRecordMutation();
  const customerId = props.call.customer?.id;
  const canOpenLinkDialog = props.canLink && isNotNil(customerId);
  const records = props.call.records;
  const hasTiles = !isEmpty(records) || canOpenLinkDialog;

  const handleUnlink = (record: TelephonyCallDetailRecord) => {
    unassignRecord({
      callId: props.call.id,
      resourceId: record.resourceId,
      recordId: record.recordId,
    })
      .unwrap()
      .catch(handleApiError);
  };

  const openLinkDialog = () => {
    if (isNil(customerId)) {
      return;
    }

    openDialog(
      <LinkCallRecordDialog
        callId={props.call.id}
        customerId={customerId}
        data-testid={suffixTestId('linkDialog', props)}
      />,
      {
        title: i18n.t('page.telephony.callDetail.linkRecordTitle', {
          name: props.call.customer?.name ?? '',
        }),
        size: LINK_DIALOG_SIZE,
        'data-testid': suffixTestId('linkDialog', props),
      }
    );
  };

  return (
    <Card
      title={i18n.t('page.telephony.callDetail.linkedRecords')}
      parameters={[i18n.t('page.telephony.callDetail.linkedRecordsCount', {count: records.length})]}
      data-testid={suffixTestId('linkedRecordsCard', props)}
    >
      <VStack spacing={4}>
        <Show when={!hasTiles}>
          <Text size="small" color="secondary" data-testid={suffixTestId('noRecords', props)}>
            {i18n.t('page.telephony.callDetail.noLinkedRecords')}
          </Text>
        </Show>
        <Show when={hasTiles}>
          <Grid columns={GRID_COLUMNS} spacing={4}>
            {records.map((record) => (
              <CallLinkedRecordTile
                key={`${record.resourceId}-${record.recordId}`}
                record={record}
                onUnlink={props.canUnlink ? handleUnlink : undefined}
                isUnlinking={
                  isUnlinking &&
                  unlinkArgs?.resourceId === record.resourceId &&
                  unlinkArgs.recordId === record.recordId
                }
                data-testid={suffixTestId(`record-[${record.recordId}]`, props)}
              />
            ))}
            <Show when={canOpenLinkDialog}>
              <LinkRecordPlaceholder
                onClick={openLinkDialog}
                data-testid={suffixTestId('linkRecord', props)}
              />
            </Show>
          </Grid>
        </Show>
      </VStack>
    </Card>
  );
}
